import { formatLocalDate } from '@/utils/localDateUtils';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Contrato } from '@/types';
import contractService from '@/services/contractService';
import DataTable, { Column } from '@/components/common/DataTable';
import MainLayout from '@/components/layout/MainLayout';
import { useAuth } from '@/contexts/AuthContext';

export default function ContractList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const clientId = searchParams.get('cliente_id');
  const [result, setResult] = useState<{ items: Contrato[]; total: number; page: number; totalPages: number }>({
    items: [],
    total: 0,
    page: 1,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const skipSearchEffect = useRef(true);

  const load = async (p = 1, searchTerm = search) => {
    setLoading(true);
    try {
      const res: any = await contractService.getContracts({
        page: p,
        limit: 25,
        ...(clientId ? { cliente_id: Number(clientId) } : {}),
        search: searchTerm.trim() || undefined,
        ...(sortColumn ? { sort: sortColumn, dir: sortDirection } : {}),
      });
      setResult(res.data);
      setPage(res.data.page);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
  }, [clientId]);

  useEffect(() => {
    if (skipSearchEffect.current) {
      skipSearchEffect.current = false;
      return;
    }

    const timeout = window.setTimeout(() => load(1, search), 300);
    return () => window.clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    load(1, search);
  }, [sortColumn, sortDirection]);

  const handleSortChange = (column: string, direction: 'asc' | 'desc') => {
    setSortColumn(column);
    setSortDirection(direction);
  };

  const columns: Column<Contrato>[] = [
    {
      key: 'numero_contrato',
      label: 'N° Contrato',
      sortable: true,
    },
    {
      key: 'correlativo',
      label: 'Correlativo',
      sortable: true,
      className: 'hidden 2xl:table-cell',
    },
    {
      key: 'fecha_inicio',
      label: 'Fecha Alquiler',
      sortable: true,
      render: (contract) => formatLocalDate(contract.fecha_inicio)
    },
    {
      key: 'monto_alquiler_propietario',
      label: 'Alquiler Prop.',
      render: (contract) => {
        return `${contract.moneda_propietario} ${Number(contract.monto_alquiler_propietario).toLocaleString()}`;
      }
    },
    {
      key: 'monto_alquiler_inquilino',
      label: 'Alquiler Inq.',
      render: (contract) => {
        return `${contract.moneda_inquilino} ${Number(contract.monto_alquiler_inquilino).toLocaleString()}`;
      }
    },
    {
      key: 'fecha_ultimo_pago_propietario',
      label: 'Fecha de pago Prop.',
      className: 'hidden 2xl:table-cell',
      render: (contract) => {
        if (contract.fecha_ultimo_pago_propietario) {
          return formatLocalDate(contract.fecha_ultimo_pago_propietario);
        }
        return '-';
      }
    },
    {
      key: 'fecha_ultimo_pago_inquilino',
      label: 'Fecha de pago Inq.',
      className: 'hidden 2xl:table-cell',
      render: (contract) => {
        if (contract.fecha_ultimo_pago_inquilino) {
          return formatLocalDate(contract.fecha_ultimo_pago_inquilino);
        }
        return '-';
      }
    },
    {
      key: 'comision',
      label: 'Comisión Administrativa',
      className: 'hidden 2xl:table-cell',
      render: (contract) => {
        const comisionAmount = (contract.monto_alquiler_inquilino * contract.comision) / 100;
        return `${contract.moneda_inquilino} ${comisionAmount.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      key: 'iva',
      label: 'IVA sobre comisión',
      className: 'hidden 2xl:table-cell',
      render: (contract) => {
        const ivaPercentage = contract.iva ?? contract.default_iva;
        if (!ivaPercentage) {
          return '-';
        }
        const comisionAmount = (contract.monto_alquiler_inquilino * contract.comision) / 100;
        const ivaAmount = (comisionAmount * ivaPercentage) / 100;
        return `${contract.moneda_inquilino} ${ivaAmount.toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      }
    },
    {
      key: 'estado',
      label: 'Estado',
      sortable: true,
      render: (contract) => {
        const statusColors: Record<string, string> = {
          BORRADOR: 'bg-gray-100 text-gray-800',
          ACTIVO: 'bg-green-100 text-green-800',
          FINALIZADO: 'bg-blue-100 text-blue-800',
          RESCINDIDO: 'bg-red-100 text-red-800'
        };
        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            statusColors[contract.estado] || 'bg-gray-100 text-gray-800'
          }`}>
            {contract.estado}
          </span>
        );
      }
    }
  ];

  const handleRowClick = (contract: Contrato) => {
    navigate(`/contracts/${contract.id}`);
  };

  return (
    <MainLayout>
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Contratos</h1>
            <p className="text-gray-600 mt-1">Gestión de contratos de alquiler</p>
          </div>
          {hasPermission('contracts', 'write') && (
            <button
              onClick={() => navigate('/contracts/new')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nuevo Contrato
            </button>
          )}
        </div>

        <DataTable
          columns={columns}
          data={result.items}
          loading={loading}
          searchable={true}
          serverSearch={true}
          serverSort={true}
          searchValue={search}
          onSearchChange={setSearch}
          sortValue={{ column: sortColumn, direction: sortDirection }}
          onSortChange={handleSortChange}
          searchPlaceholder="Buscar por número, inquilino, propiedad..."
          onRowClick={handleRowClick}
          rowKey="id"
          emptyState={
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📄</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No hay contratos registrados</h3>
              <p className="text-gray-600 mb-4">Comienza creando tu primer contrato de alquiler</p>
              {hasPermission('contracts', 'write') && (
                <button
                  onClick={() => navigate('/contracts/new')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Crear Contrato
                </button>
              )}
            </div>
          }
        />

        {result.items.length > 0 && (
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center">
            <div className="text-sm text-gray-600">
              Mostrando {((page - 1) * 25) + 1} a {Math.min(page * 25, result.total)} de {result.total} contratos
            </div>
            <div className="flex gap-2">
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page <= 1}
                onClick={() => load(page - 1, search)}
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-gray-700">
                Página {page} de {result.totalPages}
              </span>
              <button
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={page >= result.totalPages}
                onClick={() => load(page + 1, search)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
